import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  Container,
  StyledTable,
  StyledCheckbox,
  StyledTHContent,
  StyledSortingIcon,
  LoadingContainer,
  Tooltip,
  TooltipContent,
} from './table.component.styles'
import {
  SmallText,
  Body,
} from 'ggx-componentlibrary/design/typography/typography.styles'

function Table({
  className,
  uniqueIdentifier,
  columns,
  data,
  isLoading,
  loadingView,
  emptyView,
  selectable,
  selectedKeys = [],
  onSelect,
  highlightKeys = [],
  onClickTr,
  onSort,
  sortedColumn = {},
}) {
  const displayData = data.slice()

  const [isFirstLoading, setIsFirstLoading] = useState(isLoading)
  useEffect(() => {
    if (!isLoading) setIsFirstLoading(false)
  }, [isFirstLoading && isLoading])

  const selectedKeysSet = useMemo(() => {
    const dataKeys = new Set(data.map(item => item[uniqueIdentifier]))
    return new Set(selectedKeys.filter(key => dataKeys.has(key)))
  }, [data, selectedKeys.reduce((res, key) => res + '-' + key, '')])

  const selectedState = useMemo(() => {
    const selectedNum = displayData.reduce(
      (res, item) => res + Number(selectedKeysSet.has(item[uniqueIdentifier])),
      0
    )
    return {
      value: displayData.length === selectedNum,
      indeterminate: displayData.length > selectedNum && selectedNum > 0,
    }
  }, [data, selectedKeysSet])

  const highlightKeysSet = useMemo(() => {
    const dataKeys = new Set(data.map(item => item[uniqueIdentifier]))
    return new Set(highlightKeys.filter(key => dataKeys.has(key)))
  }, [data, highlightKeys.reduce((res, key) => res + '-' + key, '')])

  return (
    <Container className={className}>
      {displayData.length === 0 && !isFirstLoading && emptyView}
      {displayData.length !== 0 && !isLoading && (
        <StyledTable>
          <thead>
            <tr>
              {selectable && (
                <th data-checkbox="" width="32">
                  <StyledCheckbox
                    {...selectedState}
                    onChange={() => {
                      if (selectedState.value || selectedState.indeterminate) {
                        onSelect([])
                      } else {
                        onSelect(
                          displayData.map(item => item[uniqueIdentifier])
                        )
                      }
                    }}
                  />
                </th>
              )}
              {columns.map((column, index) => {
                const state =
                  sortedColumn.key === column.key ? sortedColumn.state : 'none'
                return (
                  <th
                    key={`${index}-${column.key}`}
                    width={column.width}
                    data-sortable={column.sortable}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.renderHead ? (
                      column.renderHead(column)
                    ) : (
                      <StyledTHContent
                        onClick={
                          column.sortable
                            ? () => {
                                onSort &&
                                  onSort(
                                    column.key,
                                    {
                                      asc: 'desc',
                                      desc: 'none',
                                      none: 'asc',
                                    }[state]
                                  )
                              }
                            : undefined
                        }
                      >
                        <SmallText typographyStyles="mb-none">
                          {column.label}
                        </SmallText>
                        {column.sortable && (
                          <StyledSortingIcon
                            state={
                              sortedColumn.key === column.key
                                ? sortedColumn.state
                                : 'none'
                            }
                          />
                        )}
                        {column.hint && (
                          <Tooltip>
                            <TooltipContent>
                              <SmallText typographyStyles="mb-none">
                                {column.hint}
                              </SmallText>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </StyledTHContent>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {displayData.map(row => (
              <tr
                key={`${row[uniqueIdentifier]}`}
                data-checkbox={selectedKeysSet.has(row[uniqueIdentifier])}
                data-highlight={highlightKeysSet.has(row[uniqueIdentifier])}
                onClick={() => {
                  onClickTr?.(row)
                }}
              >
                {selectable && (
                  <td data-checkbox="">
                    <StyledCheckbox
                      value={selectedKeysSet.has(row[uniqueIdentifier])}
                      onChange={() => {
                        onSelect(
                          displayData
                            .map(item => item[uniqueIdentifier])
                            .filter(key =>
                              key === row[uniqueIdentifier]
                                ? !selectedKeysSet.has(row[uniqueIdentifier])
                                : selectedKeysSet.has(key)
                            )
                        )
                      }}
                    />
                  </td>
                )}
                {columns.map((column, index) => (
                  <td key={`${index}-${column.key}`}>
                    {column.renderCell ? (
                      column.renderCell(row[column.key], row)
                    ) : (
                      <Body typographyStyles="mb-none">{row[column.key]}</Body>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      )}
      {isLoading && <LoadingContainer>{loadingView}</LoadingContainer>}
    </Container>
  )
}

Table.propTypes = {
  className: PropTypes.string,
  uniqueIdentifier: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sortable: PropTypes.bool,
      sortASC: PropTypes.func,
      hint: PropTypes.string,
      renderHead: PropTypes.func,
      renderCell: PropTypes.func,
    })
  ),
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  loadingView: PropTypes.node,
  emptyView: PropTypes.node,
  selectable: PropTypes.bool,
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
  onSelect: PropTypes.func,
  highlightKeys: PropTypes.arrayOf(PropTypes.string),
  onClickTr: PropTypes.func,
  onSort: PropTypes.func,
  sortedColumn: PropTypes.shape({
    key: PropTypes.string,
    state: PropTypes.oneOf(['desc', 'none', 'asc']),
  }),
}

export default Table
